import { useCallback, useEffect, useRef } from 'react'

// Dwell before a hover opens or closes a flyout - long enough that passing over a row, or through
// the gap to its flyout, doesn't trigger either.
const HOVER_OPEN_DELAY_MS = 150

const isPointInRect = (rect, x, y) => !!rect
  && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom

/**
 * Hover-to-open/close props for a kebab row whose content is a flyout (event color, page
 * language).
 *
 * Opening uses `mouseover`/`mouseout` rather than `mouseenter`/`mouseleave` because the trigger
 * lives in the kebab's portaled `DropdownMenuContent`, where React never synthesizes the
 * non-bubbling pair.
 *
 * Closing can't reuse those same events once the flyout is open. CUI's `usePopover` then calls
 * `ariaHideOutside(..., { shouldUseInert: true })`, which makes the trigger `inert` (it's a
 * portaled sibling of the flyout's popover, not a descendant). That hit-test change alone, with no
 * real pointer movement, still fires a `mouseout` on the trigger - confirmed live to carry
 * `relatedTarget: document.body`, not `null`, so it's indistinguishable from a real leave and a
 * naive close loops forever (close, un-inert, synthetic re-entry, reopen, ...).
 *
 * So the trigger side of closing is driven by real `pointermove` coordinates against the
 * trigger's own live rect instead: `inert` only affects hit-testing, not event dispatch, so this
 * only reacts to actual cursor motion. The flyout content itself is never `inert`, so its
 * `mouseenter`/`mouseleave` are trustworthy - but the pointermove check only knows the trigger's
 * rect, so `isOverContentRef` bridges the gap: set on content `mouseenter`, cleared on
 * `mouseleave`, and treated as "inside" alongside the trigger rect.
 */
export const useHoverOpenMenu = ({ isOpen, onOpen, onClose }) => {
  const openTimerRef = useRef(null)
  const closeTimerRef = useRef(null)
  const triggerRef = useRef(null)
  const isOverContentRef = useRef(false)

  const cancelPendingOpen = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])

  const cancelPendingClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const scheduleClose = useCallback(() => {
    if (closeTimerRef.current) return

    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      onClose()
    }, HOVER_OPEN_DELAY_MS)
  }, [onClose])

  useEffect(() => () => {
    cancelPendingOpen()
    cancelPendingClose()
  }, [cancelPendingOpen, cancelPendingClose])

  const handleMouseOver = useCallback((event) => {
    if (openTimerRef.current || event.currentTarget.contains(event.relatedTarget)) return

    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null
      onOpen()
    }, HOVER_OPEN_DELAY_MS)
  }, [onOpen])

  const handleMouseOut = useCallback((event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return

    cancelPendingOpen()
  }, [cancelPendingOpen])

  useEffect(() => {
    if (!isOpen) return undefined

    // Content just (re)mounted - reset rather than carry over a stale true from a previous cycle
    // whose content could unmount without ever firing mouseleave.
    isOverContentRef.current = false

    const handlePointerMove = (event) => {
      const triggerRect = triggerRef.current?.getBoundingClientRect()

      if (isOverContentRef.current || isPointInRect(triggerRect, event.clientX, event.clientY)) {
        cancelPendingClose()
      } else {
        scheduleClose()
      }
    }

    window.addEventListener('pointermove', handlePointerMove)

    return () => window.removeEventListener('pointermove', handlePointerMove)
  }, [isOpen, cancelPendingClose, scheduleClose])

  const handleContentMouseEnter = useCallback(() => {
    isOverContentRef.current = true
    cancelPendingClose()
  }, [cancelPendingClose])

  const handleContentMouseLeave = useCallback(() => {
    isOverContentRef.current = false
    if (isOpen) scheduleClose()
  }, [isOpen, scheduleClose])

  return {
    triggerProps: { ref: triggerRef, onMouseOver: handleMouseOver, onMouseOut: handleMouseOut },
    contentProps: { onMouseEnter: handleContentMouseEnter, onMouseLeave: handleContentMouseLeave },
  }
}
